import axios from 'axios';
import store from '@/store';

const LOG_BATCH_INTERVAL = 10000;
const MAX_BATCH_SIZE = 20;

let logQueue = [];
let batchTimer = null;

const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  let deviceType = 'desktop';
  if (/Mobile|Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    deviceType = /iPad/i.test(ua) ? 'tablet' : 'mobile';
  } else if (/Tablet/i.test(ua)) {
    deviceType = 'tablet';
  }

  let os = 'Unknown';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';

  let browser = 'Unknown';
  if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) browser = 'Chrome';
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Edg/i.test(ua)) browser = 'Edge';
  else if (/Opera|OPR/i.test(ua)) browser = 'Opera';

  return {
    userAgent: ua.substring(0, 500),
    deviceType,
    os,
    browser,
    screenWidth,
    screenHeight,
    viewportWidth,
    viewportHeight,
    pixelRatio: window.devicePixelRatio || 1,
    touchSupport: 'ontouchstart' in window,
    language: navigator.language
  };
};

const getAuthToken = () => {
  return localStorage.getItem('userToken');
};

const sendLogs = async () => {
  if (logQueue.length === 0) return;
  
  const logsToSend = logQueue.splice(0, MAX_BATCH_SIZE);
  
  try {
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    await axios.post('/api/logs', { logs: logsToSend }, { headers });
  } catch (error) {
    console.warn('Failed to send logs:', error.message);
    logQueue.unshift(...logsToSend);
    if (logQueue.length > 100) {
      logQueue = logQueue.slice(-100);
    }
  }
};

const startBatchTimer = () => {
  if (batchTimer) return;
  batchTimer = setInterval(() => {
    sendLogs();
  }, LOG_BATCH_INTERVAL);
};

const log = (severity, message, options = {}) => {
  const { 
    stackTrace = null, 
    component = '', 
    context = {},
    immediate = false 
  } = options;

  const logEntry = {
    severity,
    message: typeof message === 'object' ? JSON.stringify(message) : String(message),
    stackTrace,
    deviceInfo: getDeviceInfo(),
    pageUrl: window.location.href,
    component,
    context: {
      ...context,
      timestamp: new Date().toISOString(),
      activeZoneId: store?.state?.category?.activeZoneId || null
    }
  };

  logQueue.push(logEntry);
  startBatchTimer();

  if (immediate || severity === 'error' || logQueue.length >= MAX_BATCH_SIZE) {
    sendLogs();
  }
};

const logger = {
  debug(message, options = {}) {
    log('debug', message, options);
  },
  
  info(message, options = {}) {
    log('info', message, options);
  },
  
  warn(message, options = {}) {
    log('warn', message, options);
  },
  
  error(message, options = {}) {
    log('error', message, options);
  },

  captureError(error, context = {}) {
    const message = error.message || String(error);
    const stackTrace = error.stack || null;
    const { component: componentFromContext, ...restContext } = context;
    
    log('error', message, {
      stackTrace,
      component: componentFromContext || '',
      context: {
        ...restContext,
        errorName: error.name,
        errorCode: error.code
      },
      immediate: true
    });
  },

  track(eventName, data = {}) {
    log('info', `[EVENT] ${eventName}`, {
      context: data,
      component: 'analytics'
    });
  },

  flush() {
    return sendLogs();
  }
};

window.addEventListener('error', (event) => {
  logger.captureError(event.error || new Error(event.message), {
    source: 'window.onerror',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
});

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
  logger.captureError(error, {
    source: 'unhandledrejection'
  });
});

window.addEventListener('beforeunload', () => {
  if (logQueue.length > 0) {
    const payload = JSON.stringify({ logs: logQueue });
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon('/api/logs', blob);
  }
});

export default logger;

export const loggerPlugin = {
  install(app) {
    app.config.globalProperties.$logger = logger;
    app.provide('logger', logger);
    
    app.config.errorHandler = (err, instance, info) => {
      logger.captureError(err, {
        source: 'vue.errorHandler',
        componentName: instance?.$options?.name || 'Unknown',
        info
      });
      console.error('Vue Error:', err, info);
    };
  }
};
